import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Switch,
  Tooltip,
  Divider,
  Checkbox,
  Empty,
} from 'antd';
import {
  AppstoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { packagesApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const STORAGE_UNIT_OPTIONS = [
  { value: 'mb', label: 'MB' },
  { value: 'gb', label: 'GB' },
];

const PACKAGE_TYPE_OPTIONS = [
  { value: 'paid', label: 'پولی' },
  { value: 'free', label: 'رایگان' },
];

const BILLING_TYPE_OPTIONS = [
  { value: 'standard', label: 'استاندارد' },
  { value: 'lifetime', label: 'لایف‌تایم' },
];

const MODULE_LABELS_FA = {
  clients: 'مشتریان',
  employees: 'کارمندان',
  projects: 'پروژه‌ها',
  attendance: 'حضور و غیاب',
  tasks: 'وظایف',
  estimates: 'پیش‌فاکتورها',
  invoices: 'فاکتورها',
  payments: 'پرداخت‌ها',
  timelogs: 'تایم‌لاگ‌ها',
  tickets: 'تیکت‌ها',
  events: 'رویدادها',
  notices: 'اعلانات',
  leaves: 'مرخصی‌ها',
  leads: 'سرنخ‌ها',
  holidays: 'تعطیلات',
  products: 'محصولات',
  expenses: 'هزینه‌ها',
  contracts: 'قراردادها',
  reports: 'گزارشات',
  orders: 'سفارش‌ها',
  knowledgebase: 'پایگاه دانش',
  bankaccount: 'حساب بانکی',
  messages: 'پیام‌ها',
  asset: 'دارایی‌ها',
  letter: 'نامه‌ها',
  payroll: 'حقوق و دستمزد',
  performance: 'ارزیابی عملکرد',
  purchase: 'خرید',
  qrcode: 'QR Code',
  recruit: 'استخدام',
  sms: 'پیامک',
  webhooks: 'وب‌هوک',
};

const getModuleLabelFa = (nameOrId, modulesMap) => {
  const moduleName = String(modulesMap?.[String(nameOrId)]?.module_name || nameOrId || '').toLowerCase();
  return MODULE_LABELS_FA[moduleName] || moduleName || String(nameOrId);
};

const parseModulesPayload = (value) => {
  if (!value) return [];
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (Array.isArray(parsed)) {
      return parsed.map((v) => String(v));
    }
    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed).map((k) => String(k));
    }
  } catch {
    return [];
  }
  return [];
};

const toModuleObject = (selectedIds, modulesMap) =>
  selectedIds.reduce((acc, id) => {
    const key = String(id);
    if (modulesMap[key]) {
      acc[key] = modulesMap[key].module_name;
    }
    return acc;
  }, {});

const PackagesSettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [form] = Form.useForm();
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [modulesMeta, setModulesMeta] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [moduleSearch, setModuleSearch] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);

  const modulesMap = useMemo(() => {
    const map = {};
    modulesMeta.forEach((m) => {
      map[String(m.id)] = m;
    });
    return map;
  }, [modulesMeta]);

  const selectedCount = selectedModuleIds.length;

  const filteredModules = useMemo(() => {
    const q = moduleSearch.trim().toLowerCase();
    if (!q) return modulesMeta;
    return modulesMeta.filter((m) => String(m.module_name || '').toLowerCase().includes(q));
  }, [moduleSearch, modulesMeta]);

  const fetchIndex = useCallback(async (page = 1, perPage = 10) => {
    setLoading(true);
    try {
      const res = await packagesApi.getAll({ page, per_page: perPage });
      const data = res?.data || {};
      setRows(data.data || []);
      setPagination({
        current: Number(data.current_page || page),
        pageSize: Number(data.per_page || perPage),
        total: Number(data.total || 0),
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت لیست بسته‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIndex(1, 10);
  }, [fetchIndex]);

  const openCreate = async () => {
    setEditingId(null);
    setFormOpen(true);
    setFormLoading(true);
    setSelectedModuleIds([]);
    setModuleSearch('');
    form.resetFields();
    try {
      const res = await packagesApi.getCreateMeta();
      const data = res?.data || {};
      setModulesMeta(data.modules || []);
      setCurrencies(data.currencies || []);
      form.setFieldsValue({
        package_type: 'paid',
        package: 'standard',
        max_employees: 20,
        max_storage_size: 1,
        storage_unit: 'gb',
        sort: Number(data.package_count || 0) + 1,
        is_private: false,
        is_recommended: false,
        monthly_status: true,
        annual_status: false,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات اولیه');
      setFormOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setFormOpen(true);
    setFormLoading(true);
    setModuleSearch('');
    form.resetFields();
    try {
      const res = await packagesApi.getEditMeta(id);
      const data = res?.data || {};
      const pkg = data.package || {};
      const parsedModuleIds = parseModulesPayload(pkg.module_in_package);
      setModulesMeta(data.modules || []);
      setCurrencies(data.currencies || []);
      setSelectedModuleIds(parsedModuleIds);
      form.setFieldsValue({
        package_type: Number(pkg.is_free) === 1 ? 'free' : 'paid',
        name: pkg.name || '',
        package: pkg.package || 'standard',
        max_employees: pkg.max_employees,
        max_storage_size: pkg.max_storage_size,
        storage_unit: pkg.storage_unit || 'gb',
        sort: Number(pkg.sort || 0),
        is_private: Number(pkg.is_private) === 1,
        is_recommended: Number(pkg.is_recommended) === 1,
        currency_id: pkg.currency_id ? Number(pkg.currency_id) : undefined,
        monthly_status: String(pkg.monthly_status) === '1',
        annual_status: String(pkg.annual_status) === '1',
        monthly_price: Number(pkg.monthly_price || 0),
        annual_price: Number(pkg.annual_price || 0),
        description: pkg.description || '',
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات بسته');
      setFormOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await packagesApi.delete(id);
      message.success('بسته حذف شد');
      fetchIndex(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error?.message || 'خطا در حذف بسته');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedModuleIds.length) {
        message.warning('حداقل یک ماژول انتخاب کنید');
        return;
      }
      if (!values.monthly_status && !values.annual_status) {
        message.warning('حداقل یک دوره پرداخت (ماهانه/سالانه) را فعال کنید');
        return;
      }

      const payload = {
        package_type: values.package_type,
        name: String(values.name || '').trim(),
        package: values.package,
        max_employees: Number(values.max_employees || 0),
        max_storage_size: Number(values.max_storage_size || 0),
        storage_unit: values.storage_unit,
        sort: Number(values.sort || 0),
        is_private: values.is_private ? 'on' : 'off',
        is_recommended: values.is_recommended ? 'on' : 'off',
        currency_id: values.currency_id ? Number(values.currency_id) : null,
        monthly_status: values.monthly_status ? 'on' : 'off',
        annual_status: values.annual_status ? 'on' : 'off',
        module_in_package: toModuleObject(selectedModuleIds, modulesMap),
        description: values.description || '',
      };

      if (values.monthly_status) payload.monthly_price = Number(values.monthly_price || 0);
      if (values.annual_status) payload.annual_price = Number(values.annual_price || 0);

      setFormSubmitting(true);
      if (editingId) {
        await packagesApi.update(editingId, payload);
        message.success('بسته بروزرسانی شد');
      } else {
        await packagesApi.create(payload);
        message.success('بسته جدید ایجاد شد');
      }
      setFormOpen(false);
      fetchIndex(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error?.errorFields) return;
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره بسته');
    } finally {
      setFormSubmitting(false);
    }
  };

  const columns = [
    { title: 'نام بسته', dataIndex: 'name', key: 'name', width: 160 },
    {
      title: 'ظرفیت',
      key: 'capacity',
      width: 180,
      render: (_, row) => (
        <Space direction="vertical" size={2}>
          <span>{row.max_employees ?? 0} کارمند</span>
          <span className="text-xs text-gray-500">
            {row.max_storage_size ?? 0} {String(row.storage_unit || '').toUpperCase()}
          </span>
        </Space>
      ),
    },
    {
      title: 'قیمت‌ها',
      key: 'prices',
      width: 220,
      render: (_, row) => (
        <Space direction="vertical" size={2}>
          <span className="text-xs">ماهانه: {row.formatted_monthly_price || '—'}</span>
          <span className="text-xs">سالانه: {row.formatted_annual_price || '—'}</span>
        </Space>
      ),
    },
    {
      title: 'ماژول‌های بسته',
      key: 'modules',
      render: (_, row) => {
        const ids = parseModulesPayload(row.module_in_package);
        const chips = ids.slice(0, 6).map((id) => (
          <Tag key={id} className="!text-[11px] !px-2 !py-0.5 !rounded-full !m-0">
            {getModuleLabelFa(id, modulesMap)}
          </Tag>
        ));
        return ids.length ? (
          <Space size={[6, 6]} wrap>
            {chips}
            {ids.length > 6 ? (
              <Tooltip
                title={
                  <div className="max-w-[320px] flex flex-wrap gap-1">
                    {ids.map((id) => (
                      <Tag key={id} className="!text-[11px] !m-0">
                        {getModuleLabelFa(id, modulesMap)}
                      </Tag>
                    ))}
                  </div>
                }
              >
                <Tag color="blue" className="!text-[11px] !m-0">+{ids.length - 6}</Tag>
              </Tooltip>
            ) : null}
          </Space>
        ) : (
          <span className="text-xs text-gray-400">بدون ماژول</span>
        );
      },
    },
    {
      title: 'وضعیت',
      key: 'status',
      width: 120,
      render: (_, row) => (
        <Space wrap size={4}>
          {String(row.default) === 'yes' ? <Tag color="gold">پیش‌فرض</Tag> : null}
          {String(row.default) === 'trial' ? <Tag color="cyan">تریال</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      fixed: 'right',
      width: 124,
      render: (_, row) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row.id)} />
          <Popconfirm
            title="حذف این بسته؟"
            okText="حذف"
            cancelText="لغو"
            onConfirm={() => onDelete(row.id)}
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const selectedAllVisible = filteredModules.length > 0 && filteredModules.every((m) => selectedModuleIds.includes(String(m.id)));

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700/70">
                <AppstoreOutlined className="text-xl text-gray-700 dark:text-gray-300" />
              </div>
              <div>
                <h1 className="m-0 text-2xl font-bold text-gray-800 dark:text-gray-100">بسته‌ها</h1>
                <span className="text-xs text-gray-500">مدیریت پلن‌ها  </span>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchIndex(pagination.current, pagination.pageSize)}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={openCreate}>
                بسته جدید
              </Button>
            </Space>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                scroll={{ x: 1100 }}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  onChange: (page, pageSize) => fetchIndex(page, pageSize),
                }}
              />
            </Card>
          </div>
        </Card>

        <Modal
          title={editingId ? 'ویرایش بسته' : 'ایجاد بسته جدید'}
          open={formOpen}
          onCancel={() => setFormOpen(false)}
          onOk={onSubmit}
          okText="ذخیره"
          cancelText="لغو"
          confirmLoading={formSubmitting}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          width={1000}
          destroyOnClose
          className="custom-font"
        >
          <Form form={form} layout="vertical" className="mt-2" disabled={formLoading}>
            <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-3">
              <Form.Item name="name" label="نام بسته" rules={[{ required: true, message: 'نام بسته را وارد کنید' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="package_type" label="نوع بسته" rules={[{ required: true }]}>
                <Select options={PACKAGE_TYPE_OPTIONS} popupClassName="custom-font" />
              </Form.Item>
              <Form.Item name="package" label="نوع پلن" rules={[{ required: true }]}>
                <Select options={BILLING_TYPE_OPTIONS} popupClassName="custom-font" />
              </Form.Item>
              <Form.Item name="max_employees" label="حداکثر کارمند" rules={[{ required: true }]}>
                <InputNumber min={1} className="w-full" />
              </Form.Item>
              <Form.Item name="max_storage_size" label="حداکثر فضای ذخیره‌سازی" rules={[{ required: true }]}>
                <InputNumber min={0} className="w-full" />
              </Form.Item>
              <Form.Item name="storage_unit" label="واحد ذخیره‌سازی" rules={[{ required: true }]}>
                <Select options={STORAGE_UNIT_OPTIONS} popupClassName="custom-font" />
              </Form.Item>
              <Form.Item name="sort" label="ترتیب نمایش" rules={[{ required: true }]}>
                <InputNumber min={0} className="w-full" />
              </Form.Item>
              <Form.Item name="currency_id" label="واحد پول" rules={[{ required: true, message: 'ارز را انتخاب کنید' }]}>
                <Select
                  options={currencies.map((c) => ({ value: c.id, label: `${c.currency_name} (${c.currency_symbol})` }))}
                  popupClassName="custom-font"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item name="description" label="توضیحات" className="sm:col-span-2 lg:col-span-1">
                <Input.TextArea rows={3} />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Form.Item name="is_private" label="خصوصی" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="is_recommended" label="پیشنهادی" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="monthly_status" label="فعال‌بودن ماهانه" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="annual_status" label="فعال‌بودن سالانه" valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
              <Form.Item shouldUpdate noStyle>
                {() => (
                  form.getFieldValue('monthly_status') ? (
                    <Form.Item
                      name="monthly_price"
                      label="قیمت ماهانه"
                      rules={[{ required: true, message: 'قیمت ماهانه را وارد کنید' }]}
                    >
                      <InputNumber min={0} className="w-full" />
                    </Form.Item>
                  ) : null
                )}
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  form.getFieldValue('annual_status') ? (
                    <Form.Item
                      name="annual_price"
                      label="قیمت سالانه"
                      rules={[{ required: true, message: 'قیمت سالانه را وارد کنید' }]}
                    >
                      <InputNumber min={0} className="w-full" />
                    </Form.Item>
                  ) : null
                )}
              </Form.Item>
            </div>

            <Divider className="!my-2" />
            <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold">ماژول‌های موجود در بسته</div>
                </div>
                <Tag color="blue">{selectedCount} ماژول انتخاب شده</Tag>
              </div>

              <Space className="mb-3" wrap>
                <Input
                  allowClear
                  placeholder="جستجوی ماژول..."
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                  style={{ width: 260 }}
                />
                <Button
                  size="small"
                  onClick={() =>
                    setSelectedModuleIds((prev) => {
                      if (selectedAllVisible) {
                        const visibleIds = new Set(filteredModules.map((m) => String(m.id)));
                        return prev.filter((id) => !visibleIds.has(id));
                      }
                      const merged = new Set(prev);
                      filteredModules.forEach((m) => merged.add(String(m.id)));
                      return Array.from(merged);
                    })
                  }
                >
                  {selectedAllVisible ? 'لغو انتخاب نتایج' : 'انتخاب همه نتایج'}
                </Button>
                <Button size="small" onClick={() => setSelectedModuleIds([])}>پاک‌کردن انتخاب‌ها</Button>
              </Space>

              {filteredModules.length ? (
                <Checkbox.Group
                  value={selectedModuleIds}
                  onChange={(vals) => setSelectedModuleIds(vals.map((v) => String(v)))}
                  className="w-full"
                >
                  <div className="flex flex-wrap gap-2">
                    {filteredModules.map((m) => (
                      <label
                        key={m.id}
                        className="flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1.5 text-xs dark:border-gray-700"
                      >
                        <Checkbox value={String(m.id)} />
                        <span>{getModuleLabelFa(m.module_name, modulesMap)}</span>
                      </label>
                    ))}
                  </div>
                </Checkbox.Group>
              ) : (
                <Empty description="ماژولی یافت نشد" />
              )}
            </div>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default PackagesSettings;
